import React, { useEffect, useState } from 'react';

/**
 * Analog clock with the Ashop "store" logo in the center.
 * Props:
 *   size: pixel size of the clock (default 180)
 *   timezone: IANA timezone (default "Europe/Amsterdam")
 *   showDigital: also show HH:MM:SS + date below the clock (default true)
 */
const AshopClock = ({ size = 180, timezone = 'Europe/Amsterdam', showDigital = true }) => {
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), 1000);
    return () => clearInterval(id);
  }, []);

  // Time in target timezone
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: timezone,
    hour: '2-digit', minute: '2-digit', second: '2-digit', hour12: false,
  }).formatToParts(now).reduce((acc, p) => ({ ...acc, [p.type]: p.value }), {});
  const hours = parseInt(parts.hour, 10);
  const minutes = parseInt(parts.minute, 10);
  const seconds = parseInt(parts.second, 10);

  const dateLabel = new Intl.DateTimeFormat('en-US', {
    timeZone: timezone,
    weekday: 'long', month: 'short', day: 'numeric', year: 'numeric',
  }).format(now);

  // Angles (degrees) — 12 at top
  const secAngle = seconds * 6;
  const minAngle = minutes * 6 + seconds * 0.1;
  const hourAngle = ((hours % 12) * 30) + minutes * 0.5;

  const cx = 100, cy = 100, r = 92;

  // Build hour ticks (12 thick) + minute ticks
  const ticks = [];
  for (let i = 0; i < 60; i++) {
    const angle = (i * 6) * (Math.PI / 180);
    const isHour = i % 5 === 0;
    const inner = isHour ? r - 12 : r - 6;
    const outer = r - 2;
    const x1 = cx + inner * Math.sin(angle);
    const y1 = cy - inner * Math.cos(angle);
    const x2 = cx + outer * Math.sin(angle);
    const y2 = cy - outer * Math.cos(angle);
    ticks.push(
      <line
        key={i}
        x1={x1} y1={y1} x2={x2} y2={y2}
        stroke={isHour ? '#1e3a8a' : '#94a3b8'}
        strokeWidth={isHour ? 2.5 : 1}
        strokeLinecap="round"
      />
    );
  }

  // Hour numerals (12, 3, 6, 9)
  const numerals = [
    { n: 12, x: 100, y: 26 },
    { n: 3,  x: 174, y: 105 },
    { n: 6,  x: 100, y: 182 },
    { n: 9,  x: 26,  y: 105 },
  ];

  return (
    <div className="flex flex-col items-center gap-2" data-testid="ashop-clock">
      <svg width={size} height={size} viewBox="0 0 200 200" className="drop-shadow-lg">
        <defs>
          <radialGradient id="ashopFace" cx="50%" cy="40%" r="65%">
            <stop offset="0%" stopColor="#ffffff" />
            <stop offset="70%" stopColor="#f8fafc" />
            <stop offset="100%" stopColor="#e2e8f0" />
          </radialGradient>
          <linearGradient id="ashopBezel" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1e40af" />
            <stop offset="50%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#f97316" />
          </linearGradient>
          <linearGradient id="ashopCenter" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#3b82f6" />
            <stop offset="100%" stopColor="#f97316" />
          </linearGradient>
        </defs>

        {/* Outer bezel ring */}
        <circle cx={cx} cy={cy} r="98" fill="url(#ashopBezel)" />
        {/* Inner bezel */}
        <circle cx={cx} cy={cy} r="94" fill="#0f172a" />
        {/* Face */}
        <circle cx={cx} cy={cy} r={r} fill="url(#ashopFace)" />

        {/* Ticks */}
        {ticks}

        {/* Numerals */}
        {numerals.map((nm) => (
          <text
            key={nm.n}
            x={nm.x}
            y={nm.y}
            textAnchor="middle"
            dominantBaseline="middle"
            fontFamily="'Segoe UI', system-ui, sans-serif"
            fontWeight="700"
            fontSize="14"
            fill="#1e3a8a"
          >
            {nm.n}
          </text>
        ))}

        {/* Brand wordmark inside the clock face */}
        <g transform="translate(100, 68)">
          {/* Small store icon above the text */}
          <path
            d="M -7 -10 L 7 -10 L 9 -7 L -9 -7 Z
               M -7 -7 L -7 4 L 7 4 L 7 -7
               M -2.5 4 L -2.5 -1 L 2.5 -1 L 2.5 4"
            fill="none"
            stroke="#3b82f6"
            strokeWidth="1.4"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </g>
        {/* "Ashop" wordmark – the focal text */}
        <text
          x="100"
          y="92"
          textAnchor="middle"
          fontFamily="'Segoe UI', system-ui, sans-serif"
          fontWeight="800"
          fontSize="18"
          fill="url(#ashopCenter)"
          letterSpacing="0.5"
        >
          Ashop
        </text>
        <text
          x="100"
          y="105"
          textAnchor="middle"
          fontFamily="'Segoe UI', system-ui, sans-serif"
          fontWeight="600"
          fontSize="6"
          fill="#94a3b8"
          letterSpacing="2"
        >
          ROTTERDAM
        </text>

        {/* Hour hand */}
        <line
          x1={cx} y1={cy + 14}
          x2={cx} y2={cy - 48}
          stroke="#0f172a"
          strokeWidth="5"
          strokeLinecap="round"
          transform={`rotate(${hourAngle} ${cx} ${cy})`}
          style={{ transition: 'transform 0.4s cubic-bezier(0.4, 2.5, 0.6, 1)' }}
        />
        {/* Minute hand */}
        <line
          x1={cx} y1={cy + 18}
          x2={cx} y2={cy - 72}
          stroke="#1e40af"
          strokeWidth="3.5"
          strokeLinecap="round"
          transform={`rotate(${minAngle} ${cx} ${cy})`}
          style={{ transition: 'transform 0.4s cubic-bezier(0.4, 2.5, 0.6, 1)' }}
        />
        {/* Second hand */}
        <line
          x1={cx} y1={cy + 22}
          x2={cx} y2={cy - 80}
          stroke="#f97316"
          strokeWidth="1.8"
          strokeLinecap="round"
          transform={`rotate(${secAngle} ${cx} ${cy})`}
          style={{ transition: 'transform 0.15s cubic-bezier(0.4, 2.5, 0.6, 1)' }}
        />
        {/* Center cap */}
        <circle cx={cx} cy={cy} r="5" fill="#f97316" stroke="white" strokeWidth="1.5" />
        <circle cx={cx} cy={cy} r="1.5" fill="#1e3a8a" />
      </svg>

      {showDigital && (
        <div className="text-center">
          <p className="text-lg font-bold text-gray-800 tracking-wide tabular-nums" data-testid="clock-digital">
            {String(hours).padStart(2, '0')}:{String(minutes).padStart(2, '0')}
            <span className="text-orange-500">:{String(seconds).padStart(2, '0')}</span>
          </p>
          <p className="text-xs text-gray-500 mt-0.5">{dateLabel} · Rotterdam</p>
        </div>
      )}
    </div>
  );
};

export default AshopClock;
